import { useMemo, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

/* ----------------------------------------------------------------------------
 * SortableTable — the UAT table follow-up (owner UAT #4), a SHARED, reusable
 * data-list table used across the SEO data screens.
 *
 * DESIGN INVARIANTS (W2a CONTRACTS §FE):
 *  - Sortable by any column flagged `sortable` with a `sortValue` accessor.
 *    Clicking a sortable header cycles asc → desc → none (back to the rows'
 *    natural, already-loaded order). `aria-sort` is set on the active header.
 *  - NUMERIC-AWARE + NULL-HONEST: for `numeric` columns the sort is numeric;
 *    NULL / undefined / NaN values ALWAYS sort LAST in both directions — we
 *    never invent an order for a metric the provider didn't return.
 *  - PURE CLIENT-SIDE: sorts only over the rows already handed in. It NEVER
 *    fetches, never triggers a reload, and never charges a provider. Sorting a
 *    paid result set is free.
 *  - RESPONSIVE + CONTAINED: compact columns; long text / URLs get `max-w` +
 *    `truncate` with the full value in a `title` for hover. Horizontal overflow
 *    is contained inside this component's own `overflow-x-auto` wrapper (opt-in
 *    via `minWidthClass`), so a wide table scrolls WITHIN the main content and
 *    never pushes the layout under the Copilot panel.
 * -------------------------------------------------------------------------- */

export type SortDirection = 'asc' | 'desc' | 'none';

export interface SortableColumn<T> {
  /** Stable key — identifies the column for sort state + React keys. */
  key: string;
  /** Column header label. */
  header: string;
  /** Cell renderer. Render em-dashes for null metrics — never a fabricated value. */
  render: (row: T) => ReactNode;
  /** Enable click-to-sort on this header. Requires `sortValue` to take effect. */
  sortable?: boolean;
  /**
   * Value used to sort this column. `null` (or NaN) always sorts LAST, in both
   * directions — a missing metric is never given an invented order. Omit to
   * leave the column unsortable even if `sortable` is set.
   */
  sortValue?: (row: T) => number | string | null;
  /** Numeric sort (subtraction). Default is a locale-aware string compare. */
  numeric?: boolean;
  /** Right-align the column (used for numeric metrics). Default left. */
  align?: 'left' | 'right';
  /** Truncate long text with an ellipsis + `max-w` (full value shown on hover). */
  truncate?: boolean;
  /** Max-width class for a truncating column. Default `max-w-[220px]`. */
  maxWidthClass?: string;
  /** Full-value hover title for a cell (used with `truncate` on long text/URLs). */
  title?: (row: T) => string | undefined;
  /** Extra classes for this column's `<td>` cells. */
  cellClassName?: string;
}

interface SortableTableProps<T> {
  rows: T[];
  columns: SortableColumn<T>[];
  /** Stable React key per row. */
  getRowKey: (row: T, index: number) => string;
  /**
   * `min-w-[...]` class applied to the table when its columns genuinely need
   * more width than the container. Only then is the `overflow-x-auto` wrapper
   * able to scroll; omit it for narrow tables that fit without scrolling.
   */
  minWidthClass?: string;
  /** Optional caption for assistive tech (visually hidden). */
  caption?: string;
  className?: string;
}

/** True for values that must sort LAST (missing metric — never invent order). */
function isBlank(v: number | string | null | undefined): boolean {
  return v === null || v === undefined || (typeof v === 'number' && Number.isNaN(v));
}

function ariaSortValue(active: boolean, dir: SortDirection): 'ascending' | 'descending' | 'none' {
  if (!active || dir === 'none') return 'none';
  return dir === 'asc' ? 'ascending' : 'descending';
}

/** The sort-state glyph: ↕ idle · ↑ asc · ↓ desc. */
function SortGlyph({ active, dir }: { active: boolean; dir: SortDirection }) {
  const glyph = !active || dir === 'none' ? '↕' : dir === 'asc' ? '↑' : '↓';
  return (
    <span
      aria-hidden
      className={cn(
        'ml-1 inline-block text-[10px] leading-none transition-colors',
        active && dir !== 'none' ? 'text-ai' : 'text-text-tertiary/60',
      )}
    >
      {glyph}
    </span>
  );
}

export function SortableTable<T>({
  rows,
  columns,
  getRowKey,
  minWidthClass,
  caption,
  className,
}: SortableTableProps<T>) {
  // Active sort: which column key + direction. `none` restores natural order.
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDirection>('none');

  const onHeaderClick = (col: SortableColumn<T>) => {
    if (!col.sortable || !col.sortValue) return;
    if (sortKey !== col.key) {
      // New column → start ascending.
      setSortKey(col.key);
      setSortDir('asc');
      return;
    }
    // Same column → cycle asc → desc → none.
    setSortDir((prev) => (prev === 'asc' ? 'desc' : prev === 'desc' ? 'none' : 'asc'));
  };

  const sortedRows = useMemo(() => {
    if (sortKey === null || sortDir === 'none') return rows;
    const col = columns.find((c) => c.key === sortKey);
    if (!col || !col.sortValue) return rows;
    const getVal = col.sortValue;
    const numeric = col.numeric === true;
    const dirMul = sortDir === 'asc' ? 1 : -1;

    // Stable sort: decorate with the original index so equal rows (and all
    // null-metric rows parked at the bottom) keep their loaded order.
    return rows
      .map((row, index) => ({ row, index }))
      .sort((a, b) => {
        const va = getVal(a.row);
        const vb = getVal(b.row);
        const aBlank = isBlank(va);
        const bBlank = isBlank(vb);
        // Nulls ALWAYS sort last, regardless of direction.
        if (aBlank && bBlank) return a.index - b.index;
        if (aBlank) return 1;
        if (bBlank) return -1;

        let cmp: number;
        if (numeric) {
          cmp = (va as number) - (vb as number);
        } else {
          cmp = String(va).localeCompare(String(vb), 'en-GB', { numeric: true, sensitivity: 'base' });
        }
        if (cmp === 0) return a.index - b.index; // stable tie-break
        return cmp * dirMul;
      })
      .map((entry) => entry.row);
  }, [rows, columns, sortKey, sortDir]);

  return (
    <div className={cn('overflow-x-auto', className)}>
      <table className={cn('w-full border-collapse', minWidthClass)}>
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr className="border-b border-border-subtle bg-inset/50 text-left text-[11px] uppercase tracking-wide text-text-tertiary">
            {columns.map((col) => {
              const active = sortKey === col.key;
              const canSort = col.sortable === true && typeof col.sortValue === 'function';
              const right = col.align === 'right';
              return (
                <th
                  key={col.key}
                  scope="col"
                  aria-sort={canSort ? ariaSortValue(active, sortDir) : undefined}
                  className={cn('px-3 py-2 font-semibold', right && 'text-right')}
                >
                  {canSort ? (
                    <button
                      type="button"
                      onClick={() => onHeaderClick(col)}
                      className={cn(
                        'group inline-flex items-center gap-0.5 rounded-sm uppercase tracking-wide',
                        'transition-colors hover:text-text-secondary',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai focus-visible:ring-offset-1 focus-visible:ring-offset-surface',
                        right && 'flex-row-reverse',
                        active && sortDir !== 'none' && 'text-text-secondary',
                      )}
                      title={`Sort by ${col.header}`}
                    >
                      <span>{col.header}</span>
                      <SortGlyph active={active} dir={sortDir} />
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, i) => (
            <tr
              key={getRowKey(row, i)}
              className="border-b border-border-subtle last:border-0 hover:bg-hover"
            >
              {columns.map((col) => {
                const right = col.align === 'right';
                const title = col.title?.(row);
                return (
                  <td
                    key={col.key}
                    title={title}
                    data-tabular={col.numeric ? '' : undefined}
                    className={cn(
                      'px-3 py-2.5 text-sm',
                      right && 'text-right',
                      col.truncate && (col.maxWidthClass ?? 'max-w-[220px]'),
                      col.truncate && 'truncate',
                      col.cellClassName ?? 'text-text-secondary',
                    )}
                  >
                    {col.render(row)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
